import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import languages from "./languages.json";
import { AnimatePresence, motion } from "framer-motion";
import useCloseWhenClickedOutside from "../../../../../../hooks/useCloseWhenClickedOutside";
import FiChevronDownSvg from "./FiChevronDownSvg";

export default function CustomLanguageSelector() {
	const { t, i18n } = useTranslation();
	const [showList, setShowList] = useState(false);
	const currentLang = i18n.language.slice(0, 2);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const languagesListRef = useRef<HTMLUListElement>(null);

	const handleChange = (code: string) => {
		if (code !== currentLang) i18n.changeLanguage(code);
		setShowList(false);
	};

	useCloseWhenClickedOutside({
		refs: [languagesListRef, buttonRef],
		setState: setShowList,
	})

	return (
		<div className="relative inline-block text-[80%] font-semibold">
			<button
				ref={buttonRef}
				onClick={() => setShowList((prev) => !prev)}
				aria-haspopup="listbox"
				aria-expanded={showList}
				aria-label={t("navBarItems.toolsMenu.language.title")}
				className="uppercase cursor-pointer flex items-center gap-1 2xl:gap-[0.5vw] whitespace-nowrap 2xl:[word-spacing:0.3vw]"
			>
				{languages.find((l) => l.code === currentLang)?.label || "🌐"}
				<FiChevronDownSvg />
			</button>

			<AnimatePresence>
				{showList && (
					<motion.ul
						ref={languagesListRef}
						role="listbox"
						className={`
							origin-top
							absolute top-full left-0 z-10 
							rounded card-multi-section dark:!bg-[#E0E0E0DD]
							text-left 
							overflow-auto 
							max-h-[30vh] 2xl:max-h-[50vh] w-max
						`}
						initial={{ opacity: 0, scale: 0 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						{languages.map((lang) => (
							<li
								key={lang.code}
								role="option"
								aria-disabled={!lang.enabled}
								aria-selected={lang.code === currentLang}
								onClick={() =>
									lang.enabled && handleChange(lang.code)
								}
								className={`grid grid-cols-[1.2em_auto] items-center gap-0 px-2 2xl:px-[0.7vw] py-2 2xl:py-[0.7vw] ${
									lang.enabled
										? "hover:bg-blue-100 dark:hover:bg-blue-800 cursor-pointer"
										: "text-gray-400 cursor-not-allowed"
								}`}
							>
								<span className="text-[80%] text-black">
									{lang.code === currentLang ? "✓" : ""}
								</span>
								<span className="2xl:[word-spacing:0.3vw]">
									{lang.label}
								</span>
							</li>
						))}
					</motion.ul>
				)}
			</AnimatePresence>
		</div>
	);
}
